import DOMPurify from 'dompurify';
import type { ChatSession, ChatMessage, ChatError } from '../store/chatBotSlice';

// FIX: Proper type definitions instead of 'any'
export interface ChatResponseData {
  response: string;
  timestamp: string;
  confidence?: string;
  intent?: string;
  actions_taken?: string[];
  follow_up_required?: boolean;
  raw_data?: Record<string, unknown>;
}

export interface ChatResponse {
  success: boolean;
  data?: ChatResponseData;  // ✅ Replaced 'any' with proper type
  error?: ChatError;
  userMessage: ChatMessage;
  agentResponse: ChatMessage;
}

export interface SessionResponse {
  success: boolean;
  session?: ChatSession;
  error?: ChatError;
}

export interface ServiceHealth {
  status: 'healthy' | 'degraded' | 'down';
  timestamp: Date;
  responseTime: number;
}

class ChatBotAPIService {
  private baseURL: string;
  private timeout: number = 30000;
  private retryAttempts: number = 3;
  
  constructor() {
    this.baseURL = this.getEnvironmentConfig().apiUrl;
  }
  
  private getEnvironmentConfig() {
    // Get API URL from environment configuration
    const apiUrl = typeof window !== 'undefined' && (window as any).__env?.apiUrl ? 
      (window as any).__env.apiUrl : 
      'http://localhost:8867/finpro_admin/v1/';
      
    return {
      apiUrl,
      timeout: 30000,
      retryAttempts: 3
    };
  }
  
  private getAuthHeaders(): HeadersInit {
    const token = this.getAuthToken();
    
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }
  
  private getAuthToken(): string {
    // Integrate with existing Angular auth system
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('finpro_k_Authorization') || 
             sessionStorage.getItem('authToken') || 
             localStorage.getItem('authToken') || 
             '';
    }
    return '';
  }
  
  private getUserContext() {
    if (typeof window === 'undefined') return {};
    
    // Use same method as Header component
    try {
      const userinfoList = sessionStorage.getItem('loggedUserInfo');
      if (userinfoList) {
        const userinfo = JSON.parse(atob(userinfoList));
        return {
          userId: userinfo['user_id'] || userinfo['id'] || '',
          userName: userinfo['name'] || '',
          userRole: userinfo['role_name'] || userinfo['role'] || '',
          company: sessionStorage.getItem('company') || '',
          environment: sessionStorage.getItem('environment') || 'production',
          tspEnvironment: sessionStorage.getItem('tspEnvironment') || 'production'
        };
      }
    } catch (error) {
      console.error('ChatBotAPI - Error parsing user info:', error);
    }
    
    // Fallback to direct sessionStorage access
    return {
      userId: sessionStorage.getItem('currentUserId') || '',
      userName: sessionStorage.getItem('currentUserName') || '',
      userRole: sessionStorage.getItem('userRole') || '',
      company: sessionStorage.getItem('company') || '',
      environment: sessionStorage.getItem('environment') || 'production',
      tspEnvironment: sessionStorage.getItem('tspEnvironment') || 'production'
    };
  }
  

  // Simple health request method to avoid CORS issues
  private async makeHealthRequest(): Promise<any> {
    const url = `${this.baseURL}api/v1/tsp/chat/health`;
    const token = this.getAuthToken();
    
    console.log('ChatBot - Making direct health request to:', url);
    // FIX: Never log actual tokens - security risk
    if (process.env.NODE_ENV === 'development') {
      console.log('ChatBot - Token present:', !!token, '| Length:', token?.length);
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    
    console.log('ChatBot - Health response status:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('ChatBot - Health response data:', data);
    
    return data;
  }
  
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}api/v1/tsp/chat/${endpoint}`;
    const config: RequestInit = {
      headers: this.getAuthHeaders(),
      ...options,
    };
    
    console.log('ChatBot - makeRequest called with URL:', url);
    console.log('ChatBot - Request config:', config);
    
    let lastError: Error;
    
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        console.log(`ChatBot - Attempt ${attempt}: Making fetch request to:`, url);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        
        const response = await fetch(url, {
          ...config,
          signal: controller.signal,
        });
        
        console.log('ChatBot - Fetch response received:', response.status, response.statusText);
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data as T;
        
      } catch (error) {
        lastError = error as Error;
        console.warn(`API request attempt ${attempt} failed:`, error);
        
        if (attempt < this.retryAttempts) {
          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError!;
  }
  
  // Session Management
  async createSession(): Promise<SessionResponse> {
    try {
      const userContext = this.getUserContext();

      console.log('ChatBotAPI - Creating session with context:', userContext);
      // FIX: Never log actual tokens - security risk
      if (process.env.NODE_ENV === 'development') {
        console.log('ChatBotAPI - Auth token present:', !!this.getAuthToken());
      }

      const requestBody = {
        userId: userContext.userId,
        userName: userContext.userName,
        userRole: userContext.userRole,
        environment: userContext.environment,
        userContext: {
          company: userContext.company,
          department: 'Operations'
        }
      };

      console.log('ChatBotAPI - Request body for session creation:', requestBody);

      const response = await this.makeRequest<any>('session', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      console.log('ChatBotAPI - Session creation response:', response);

      if (response.success && response.data) {
        // ✅ Handle new snake_case response structure
        const session: ChatSession = {
          id: response.data.session_id,  // ✅ Changed from sessionId
          userId: response.data.user_id || userContext.userId,  // ✅ Changed from userId
          userName: response.data.user_name || userContext.userName,  // ✅ Changed from userName
          createdAt: new Date(response.data.created_at),  // ✅ Changed from startTime
          lastActivity: new Date(),
          status: response.data.status,
          capabilities: response.data.capabilities  // ✅ New field
        };

        // Store session in sessionStorage for persistence
        sessionStorage.setItem('chatBot_sessionId', session.id);
        sessionStorage.setItem('chatBot_sessionData', JSON.stringify(session));
        sessionStorage.setItem('chatBot_createdAt', response.data.created_at);  // ✅ Store created_at

        console.log('ChatBotAPI - Session created successfully:', session);
        return { success: true, session };
      } else {
        throw new Error(response.message || 'Failed to create session');
      }

    } catch (error) {
      console.error('ChatBotAPI - Create session failed:', error);
      throw error;
    }
  }
  
  async validateSession(sessionId: string): Promise<SessionResponse> {
    try {
      console.log('ChatBotAPI - Validating session:', sessionId);

      const response = await this.makeRequest<any>(`session/${sessionId}`);

      console.log('ChatBotAPI - Session validation response:', response);

      // ✅ Updated to match new response structure
      if (response.success && response.data && response.data.status === 'active') {
        const session: ChatSession = {
          id: response.data.session_id,  // ✅ Changed from sessionId
          userId: response.data.user_id,  // ✅ Changed from userId
          userName: response.data.user_name || 'User',  // ✅ Changed from userName
          createdAt: new Date(response.data.created_at),  // ✅ Changed from startTime
          lastActivity: new Date(),
          status: response.data.status,
          capabilities: response.data.capabilities  // ✅ New field
        };

        return { success: true, session };
      } else {
        return {
          success: false,
          error: {
            code: 'INVALID_SESSION',
            message: 'Session not found or expired',
            timestamp: new Date(),
            recoverable: false
          }
        };
      }
    } catch (error) {
      console.error('ChatBotAPI - Validate session failed:', error);
      return {
        success: false,
        error: {
          code: 'VALIDATION_ERROR',
          message: error instanceof Error ? error.message : 'Session validation failed',
          timestamp: new Date(),
          recoverable: true
        }
      };
    }
  }
  
  async closeSession(sessionId: string): Promise<void> {
    try {
      await this.makeRequest<any>(`session/${sessionId}/close`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Close session failed:', error);
      // Don't throw - session cleanup should be graceful
    }
  }
  
  // Messaging
  async sendMessage(message: string, sessionId: string): Promise<ChatResponse> {
    try {
      const startTime = Date.now();

      console.log('ChatBotAPI - Sending message:', { message, sessionId });

      const response = await this.makeRequest<any>('message', {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          message,
          urgency: 'medium',
          metadata: {
            userAgent: navigator.userAgent,
            timestamp: Date.now(),
            messageType: 'text'
          }
        }),
      });

      console.log('ChatBotAPI - Message response:', response);

      const processingTime = Date.now() - startTime;

      if (response.success && response.data) {
        // ✅ Generate IDs for messages (API doesn't return message IDs separately)
        const userMsgId = `msg_user_${Date.now()}`;
        const assistantMsgId = `msg_assistant_${Date.now()}`;

        const userMessage: ChatMessage = {
          id: userMsgId,
          sessionId,
          role: 'user',  // ✅ Changed from 'sender' to 'role'
          content: message,
          timestamp: new Date(),
          status: 'delivered',
          messageType: 'text'
        };

        // FIX: Sanitize content at API boundary to prevent XSS
        // Sanitize BEFORE storing in Redux/sessionStorage
        const sanitizedContent = DOMPurify.sanitize(response.data.response || '', {
          ALLOWED_TAGS: ['br', 'p', 'strong', 'em', 'a'],
          ALLOWED_ATTR: ['href', 'target', 'rel'],
          ALLOW_DATA_ATTR: false
        });

        const agentResponse: ChatMessage = {
          id: assistantMsgId,
          sessionId,
          role: 'assistant',
          content: sanitizedContent,  // ✅ Sanitized content
          timestamp: new Date(response.data.timestamp || new Date()),
          status: 'delivered',
          messageType: 'text',
          context: {
            confidence: response.data.confidence,
            intent: response.data.intent,
            actions_taken: response.data.actions_taken,
            follow_up_required: response.data.follow_up_required,
            processingTimeMs: processingTime
          },
          raw_data: response.data.raw_data
        };

        return {
          success: true,
          userMessage,
          agentResponse,
          data: response.data
        };
      } else {
        throw new Error(response.message || 'Failed to send message');
      }

    } catch (error) {
      console.error('ChatBotAPI - Send message failed:', error);
      throw error;
    }
  }
  
  async getMessageHistory(sessionId: string, limit: number = 50, offset: number = 0): Promise<ChatMessage[]> {
    try {
      console.log('ChatBotAPI - Getting message history for session:', sessionId);

      const response = await this.makeRequest<any>(
        `session/${sessionId}/history?limit=${limit}&offset=${offset}`
      );

      console.log('ChatBotAPI - Message history response:', response);

      if (response.success && response.data?.messages) {
        // ✅ Map new response structure with 'role' field
        const messages = response.data.messages.map((msg: any, index: number) => ({
          id: `msg_${msg.role}_${index}`,  // Generate ID since API doesn't return one
          sessionId: sessionId,
          role: msg.role,  // ✅ Use 'role' field directly ('user' or 'assistant')
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          status: 'delivered',  // History messages are always delivered
          messageType: 'text',
          context: msg.context,  // ✅ Changed from 'metadata' to 'context'
          raw_data: msg.raw_data  // ✅ Include raw_data if present
        }));

        console.log('ChatBotAPI - Parsed message history:', messages);
        return messages;
      }

      return [];
    } catch (error) {
      console.error('ChatBotAPI - Get message history failed:', error);
      return [];
    }
  }
  
  // Health Check
  async checkServiceHealth(): Promise<ServiceHealth> {
    const startTime = Date.now();

    try {
      console.log('ChatBot - Health check starting...');
      console.log('ChatBot - baseURL:', this.baseURL);
      console.log('ChatBot - Full URL will be:', `${this.baseURL}api/v1/tsp/chat/health`);
      // FIX: Never log actual tokens - security risk
      if (process.env.NODE_ENV === 'development') {
        console.log('ChatBot - Auth token present:', !!this.getAuthToken());
      }

      const response = await this.makeHealthRequest();

      return {
        status: response.success ? 'healthy' : 'down',
        timestamp: new Date(),
        responseTime: Date.now() - startTime
      };
    } catch (error) {
      return {
        status: 'down',
        timestamp: new Date(),
        responseTime: Date.now() - startTime
      };
    }
  }

  // Freshdesk Ticket Creation
  async createFreshdeskTicket(sessionId: string): Promise<any> {
    try {
      const userContext = this.getUserContext();

      console.log('ChatBotAPI - Creating Freshdesk ticket for session:', sessionId);

      const requestBody = {
        sessionId,
        userId: userContext.userId,
        userName: userContext.userName,
        metadata: {
          userRole: userContext.userRole,
          company: userContext.company,
          environment: userContext.environment
        }
      };

      console.log('ChatBotAPI - Freshdesk ticket request:', requestBody);

      const response = await this.makeRequest<any>('create-freshdesk-ticket', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      console.log('ChatBotAPI - Freshdesk ticket response:', response);

      return response;

    } catch (error) {
      console.error('ChatBotAPI - Create Freshdesk ticket failed:', error);
      throw error;
    }
  }

}

export const chatBotAPI = new ChatBotAPIService();